import { headKey, keyBetween, spreadKeys, tailKey } from "@/lib/lex";
import { nextOccurrence } from "@/lib/nextOccurrence";
import { byPosition, compareKeysNullsLast } from "@/lib/views/order";
import { applyMyDayScheduling } from "@/lib/mutators/myDayScheduling";
import type { Mutation, MutationArgs, MutatorContext, MutatorState, OutboxOp } from "@/lib/mutators/types";
import type { SyncList, SyncTask } from "@/stores/taskStore";

// Predicts what Sync::ApplyMutation will do, as far as it is cheap: the server stays the
// authority and the next pull corrects the rows. Not predicted: a date read from the
// title, the «later» keywords, title overflow into notes, respreads of sibling keys.
// An op the server would reject leaves the rows as they are.

interface Draft {
  tasks: Record<string, SyncTask>;
  lists: Record<string, SyncList>;
}

const keptRows = (draft: Draft) => Object.values(draft.tasks).filter((t) => !t.discardedAt);

function kept(draft: Draft, id: string | null | undefined): SyncTask | null {
  const row = id ? draft.tasks[id] : undefined;
  return row && !row.discardedAt ? row : null;
}

function descendantIds(draft: Draft, id: string): string[] {
  const children = new Map<string, string[]>();
  for (const row of keptRows(draft)) {
    if (row.parentId === null) continue;
    const siblings = children.get(row.parentId);
    if (siblings) siblings.push(row.id);
    else children.set(row.parentId, [row.id]);
  }
  const out: string[] = [];
  const queue = [...(children.get(id) ?? [])];
  while (queue.length > 0) {
    const next = queue.shift()!;
    out.push(next);
    queue.push(...(children.get(next) ?? []));
  }
  return out;
}

function minKey(keys: (string | null)[]): string | null {
  return keys.reduce<string | null>((min, key) => (key !== null && (min === null || key < min) ? key : min), null);
}

function maxKey(keys: string[]): string | null {
  return keys.reduce<string | null>((max, key) => (max === null || key > max ? key : max), null);
}

// Task#ordering_space: a parent's children, or the list's roots (every template for a template).
function orderingSpace(draft: Draft, row: Pick<SyncTask, "parentId" | "listId" | "reusable">): SyncTask[] {
  return keptRows(draft).filter((t) => {
    if (row.parentId !== null) return t.parentId === row.parentId;
    if (t.parentId !== null) return false;
    return row.reusable ? t.reusable : t.listId === row.listId && !t.reusable;
  });
}

// List#tail_task_position.
function listTail(draft: Draft, listId: string): string {
  return tailKey(maxKey(keptRows(draft).filter((t) => t.listId === listId && t.status === "pending").map((t) => t.position)));
}

const inMyDayFor = (t: SyncTask, date: string) => (t.myDayOn === date || t.dueOn === date) && t.myDayDismissedOn !== date;

// MyDayPositioning.space.
function myDayHead(draft: Draft, date: string): string {
  const keys = keptRows(draft).filter((t) => !t.reusable && inMyDayFor(t, date)).map((t) => t.myDayPosition);
  return headKey(minKey(keys));
}

// MyDayPositioning.board / .future_board in Task.my_day_ordered order.
function myDayBoard(draft: Draft, date: string, today: string): SyncTask[] {
  const inOrder = (t: SyncTask) => Number(t.myDaySequentialOn === date);
  return keptRows(draft)
    .filter((t) => t.status === "pending" && !t.reusable && inMyDayFor(t, date) && (date <= today || t.myDayOn !== today))
    .sort((a, b) => inOrder(a) - inOrder(b) || compareKeysNullsLast(a.myDayPosition, b.myDayPosition) || byPosition(a, b));
}

export function listOfKind(state: { lists: Record<string, SyncList> }, kind: SyncList["kind"]): SyncList | null {
  return Object.values(state.lists).find((l) => l.kind === kind && !l.discardedAt) ?? null;
}

function isValid(t: SyncTask): boolean {
  if (t.title.trim() === "") return false;
  return !(t.reusable && (t.parentId !== null || t.recurrenceRule));
}

// One save of a task with the model callbacks that touch what the client keeps: recurrence
// anchoring, MyDayScheduling, Inbox→Later shelving and the list cascade to the subtree.
function save(draft: Draft, before: SyncTask | null, attrs: SyncTask, ctx: MutatorContext): SyncTask | null {
  const next: SyncTask = { ...attrs, updatedAt: ctx.now };
  const changed = (key: keyof SyncTask) => (before ? before[key] !== next[key] : next[key] !== null);

  if (changed("recurrenceRule") && next.recurrenceRule && !next.dueOn) next.dueOn = ctx.today;
  if (!isValid(next)) return null;
  applyMyDayScheduling(before, next, ctx.today);

  const newlyDatedInInbox =
    before && next.parentId === null && !next.reusable && next.dueOn && before.dueOn === null && draft.lists[next.listId]?.kind === "inbox";
  const later = newlyDatedInInbox ? listOfKind(draft, "later") : null;
  if (later) {
    next.listId = later.id;
    next.position = listTail(draft, later.id);
  }

  draft.tasks[next.id] = next;
  if (before && before.listId !== next.listId) {
    for (const id of descendantIds(draft, next.id)) draft.tasks[id] = { ...draft.tasks[id], listId: next.listId };
  }
  return next;
}

// Several saves in one server transaction: either all of them land or none.
function transaction(draft: Draft, run: () => boolean): void {
  const snapshot = draft.tasks;
  draft.tasks = { ...snapshot };
  if (!run()) draft.tasks = snapshot;
}

// Task#move_to_list!: a subtask named into a list is promoted to a root of it.
function moveToList(draft: Draft, row: SyncTask, list: SyncList, ctx: MutatorContext): boolean {
  if (row.parentId === null && row.listId === list.id) return true;
  return save(draft, row, { ...row, parentId: null, listId: list.id, position: listTail(draft, list.id) }, ctx) !== null;
}

function editAsTyped(draft: Draft, row: SyncTask, fields: Partial<SyncTask>, list: SyncList | null, ctx: MutatorContext): boolean {
  const saved = save(draft, row, { ...row, ...fields }, ctx);
  if (!saved) return false;
  return list ? moveToList(draft, saved, list, ctx) : true;
}

function setRows(draft: Draft, ids: string[], attrs: Partial<SyncTask>): void {
  for (const id of ids) draft.tasks[id] = { ...draft.tasks[id], ...attrs };
}

function discardSubtree(draft: Draft, id: string, ctx: MutatorContext): void {
  if (!kept(draft, id)) return;
  setRows(draft, [id, ...descendantIds(draft, id)], { discardedAt: ctx.now });
}

function withoutUndefined<T extends object>(fields: T): Partial<T> {
  return Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== undefined)) as Partial<T>;
}

function targetList(draft: Draft, listId: string | undefined | null): SyncList | null | undefined {
  if (!listId) return null;
  const list = draft.lists[listId];
  return list && !list.discardedAt ? list : undefined;
}

const mutators: { [N in keyof MutationArgs]: (draft: Draft, args: MutationArgs[N], ctx: MutatorContext) => void } = {
  createTask(draft, args, ctx) {
    if (draft.tasks[args.id]) return;
    const parent = kept(draft, args.parentId);
    if (args.parentId && !parent) return;
    const list = parent ? draft.lists[parent.listId] : targetList(draft, args.listId);
    if (!list) return;

    const reusable = args.reusable ?? false;
    const dueOn = args.dueOn ?? null;
    // User#capture_bucket's rule for a dated capture.
    const capturedTo = !parent && list.kind === "inbox" && !reusable && dueOn ? (listOfKind(draft, "later") ?? list) : list;
    const myDayOn = args.myDayOn ?? null;
    const placement = { parentId: parent?.id ?? null, listId: capturedTo.id, reusable };
    save(draft, null, {
      id: args.id,
      ...placement,
      title: args.title,
      notes: null,
      status: "pending",
      position: headKey(minKey(orderingSpace(draft, placement).map((t) => t.position))),
      myDayPosition: myDayOn ? myDayHead(draft, myDayOn) : null,
      myDayOn,
      myDayDismissedOn: null,
      myDaySequentialOn: null,
      dueOn,
      recurrenceRule: args.recurrenceRule ?? null,
      visibleFrom: null,
      completedAt: null,
      lastCompletedAt: null,
      discardedAt: null,
      createdAt: ctx.now,
      updatedAt: ctx.now,
    }, ctx);
  },

  updateTask(draft, { id, fields }, ctx) {
    const row = kept(draft, id);
    const { listId, ...attrs } = withoutUndefined(fields);
    const list = targetList(draft, listId);
    if (!row || list === undefined) return;
    transaction(draft, () => editAsTyped(draft, row, attrs, list, ctx));
  },

  completeTask(draft, { id, expectedDueOn }, ctx) {
    const row = kept(draft, id);
    if (!row || row.status === "completed") return;
    const descendants = descendantIds(draft, id);

    if (row.recurrenceRule) {
      if (expectedDueOn && row.dueOn !== expectedDueOn) return;
      const dueOn = nextOccurrence(row.recurrenceRule, row.dueOn ?? ctx.today);
      if (dueOn) {
        setRows(draft, descendants, { status: "pending", completedAt: null });
        save(draft, row, { ...row, status: "pending", completedAt: null, lastCompletedAt: ctx.now, dueOn, myDayOn: null }, ctx);
        return;
      }
    }
    setRows(draft, descendants.filter((d) => draft.tasks[d].status === "pending"), { status: "completed", completedAt: ctx.now });
    save(draft, row, { ...row, status: "completed", completedAt: ctx.now }, ctx);
  },

  uncompleteTask(draft, { id }, ctx) {
    const row = kept(draft, id);
    if (!row || row.status === "pending") return;
    save(draft, row, { ...row, status: "pending", completedAt: null }, ctx);
  },

  deleteTask(draft, { id }, ctx) {
    discardSubtree(draft, id, ctx);
  },

  reorderTask(draft, { id, beforeId, afterId }, ctx) {
    const row = kept(draft, id);
    if (!row || row.status !== "pending") return;
    const siblings = orderingSpace(draft, row).filter((t) => t.id !== id);
    const prev = siblings.find((t) => t.id === beforeId);
    const next = siblings.find((t) => t.id === afterId);
    save(draft, row, { ...row, position: keyBetween(prev?.position ?? null, next?.position ?? null) }, ctx);
  },

  reorderMyDay(draft, { id, beforeId, afterId, sequential = null, date }, ctx) {
    const day = date ?? ctx.today;
    const row = kept(draft, id);
    if (!row || row.status !== "pending") return;
    if (day < ctx.today || (day > ctx.today && sequential !== null)) return;
    let board = myDayBoard(draft, day, ctx.today);
    if (!board.some((t) => t.id === id)) return;

    if (board.some((t) => t.myDayPosition === null)) {
      const keys = spreadKeys(board.length);
      board = board.map((t, i) => (draft.tasks[t.id] = { ...t, myDayPosition: keys[i] }));
    }
    const moved = { ...draft.tasks[id] };
    if (sequential !== null) moved.myDaySequentialOn = sequential ? day : null;
    const inOrder = (t: SyncTask) => t.myDaySequentialOn === day;
    const others = board.filter((t) => t.id !== id && inOrder(t) === inOrder(moved));
    const prev = others.find((t) => t.id === beforeId);
    const next = others.find((t) => t.id === afterId);
    moved.myDayPosition = keyBetween(prev?.myDayPosition ?? null, next?.myDayPosition ?? null);
    draft.tasks[id] = { ...moved, updatedAt: ctx.now };
  },

  bulkPatch(draft, { ids, fields }, ctx) {
    const { listId, ...attrs } = withoutUndefined(fields);
    const list = targetList(draft, listId);
    if (list === undefined) return;
    const hasAttrs = Object.keys(attrs).length > 0;
    transaction(draft, () =>
      [...new Set(ids)].every((id) => {
        const row = kept(draft, id);
        if (!row) return true;
        const saved = hasAttrs ? save(draft, row, { ...row, ...attrs }, ctx) : row;
        if (!saved) return false;
        return list ? moveToList(draft, saved, list, ctx) : true;
      }),
    );
  },

  bulkDelete(draft, { ids }, ctx) {
    for (const id of ids) discardSubtree(draft, id, ctx);
  },
};

function applyInPlace(draft: Draft, mutation: Mutation, ctx: MutatorContext): void {
  (mutators[mutation.name] as (draft: Draft, args: Mutation["args"], ctx: MutatorContext) => void)(draft, mutation.args, ctx);
}

export function applyOp(state: MutatorState, mutation: Mutation, ctx: MutatorContext): MutatorState {
  const draft = { tasks: { ...state.tasks }, lists: state.lists };
  applyInPlace(draft, mutation, ctx);
  return { tasks: draft.tasks, lists: state.lists };
}

// The server rows with the ops it has not confirmed yet replayed on top, each at its own time.
export function rebase(
  tasks: Record<string, SyncTask>,
  lists: Record<string, SyncList>,
  pending: readonly OutboxOp[],
  today: string,
): Record<string, SyncTask> {
  if (pending.length === 0) return tasks;
  const draft = { tasks: { ...tasks }, lists };
  for (const op of pending) applyInPlace(draft, op, { today, now: op.createdAt });
  return draft.tasks;
}
